<template>
  <section class="screen">
    <step-header step="capture-image" :hidden="editing" />

    <div class="s-body stack">
      <camera-capture
        ref="camera"
        :active="cameraActive"
        :shot="shot"
        :guide="!manualUnlocked"
        @framing="onFraming"
        @ready="onCameraReady"
        @denied="onCameraDenied"
        @framed="onFramed"
        @unavailable="onDetectorUnavailable"
      />

      <div v-if="!shot" class="helper">
        <base-icon name="sun" />
        <span v-if="manualUnlocked">
          تشخیص خودکار چهره ممکن نشد؛ عکس را با رعایت راهنما ثبت کنید.
        </span>
        <span v-else>در محیطی با نور کافی قرار بگیرید و مستقیم به دوربین نگاه کنید.</span>
      </div>

      <div v-else class="stack gap-6 center" style="margin-top: 16px">
        <h2>آیا تصویر واضح است؟</h2>
        <p>
          در صورتی که چهره‌ی شما به‌طور کامل و واضح در کادر قرار دارد، ادامه دهید. در غیر این
          صورت، عکس را مجدداً ثبت کنید.
        </p>
      </div>
    </div>

    <div class="s-foot">
      <template v-if="!shot">
        <div class="btnrow">
          <button class="btn btn-outline guide" type="button" @click="guideOpen = true">
            <base-icon name="help" />
            <span>راهنما</span>
          </button>
          <button
            class="btn btn-primary shoot"
            type="button"
            :disabled="!canShoot"
            :aria-describedby="canShoot ? null : 'shoot-help'"
            @click="shoot"
          >
            <base-icon name="camera" />
            <span>ثبت عکس</span>
          </button>
          <!-- A disabled control with no stated reason is a dead end for anyone who
               cannot see the ring; this carries the same instruction as the chip. -->
          <span id="shoot-help" class="sr-only">{{ framingHint }}</span>
        </div>
      </template>

      <template v-else>
        <button class="btn btn-primary" type="button" @click="confirm">تأیید و ادامه</button>
        <button class="btn btn-outline" type="button" @click="retake">
          <base-icon name="refresh" />
          <span>ثبت مجدد عکس</span>
        </button>
      </template>
    </div>

    <permission-gate :state="gate" :busy="requesting" @grant="grant" @retry="grant" />
    <photo-guide-sheet :open="guideOpen" @close="guideOpen = false" />
  </section>
</template>

<script>
import StepHeader from "@/components/StepHeader.vue";
import CameraCapture from "@/components/CameraCapture.vue";
import PermissionGate from "@/components/PermissionGate.vue";
import PhotoGuideSheet from "@/components/PhotoGuideSheet.vue";
import BaseIcon from "@/components/BaseIcon.vue";
import { FLOW_ORDER, FRAMING_FALLBACK_MS } from "@/constants";
import { mediaAccessGranted } from "@/utils/device";
import { playReadyCue } from "@/utils/cue";

/**
 * Step 1 — the face photo.
 *
 * Two states in one view (`live` and `review`), because reviewing the shot is not a
 * different place; it is the same frame with a still in it. Making it a route would put a
 * history entry between taking a photo and confirming it.
 *
 * The permission gate opens on arrival rather than the camera opening itself: the browser
 * prompt has to follow a user gesture, and an unexplained prompt is the single most common
 * reason people refuse access. Once access exists the gate stays shut — see `created`.
 */
export default {
  name: "CaptureImageView",
  components: { StepHeader, CameraCapture, PermissionGate, PhotoGuideSheet, BaseIcon },

  data() {
    return {
      /** "closed" | "priming" | "blocked" */
      gate: "closed",
      cameraActive: false,
      cameraReady: false,
      requesting: false,
      guideOpen: false,
      /** The user has tapped "allow" on our own sheet at least once on this screen. */
      primed: false,
      /** The shot under review; empty means the live preview is showing. */
      shot: "",
      /** A face is steadily inside the guide oval. */
      framed: false,
      /** The framing check never succeeded here, so the shutter was handed back. */
      manualUnlocked: false,
      /** The current instruction, mirrored here so assistive tech can read it. */
      framingHint: "",
    };
  },

  computed: {
    /** Re-entered from the review screen to change just this item. */
    editing() {
      return this.$route.query.edit === "1";
    },

    /**
     * The shutter waits for a framed face.
     *
     * This is the whole point of the check: a photo taken off-centre or from across the
     * room is rejected downstream, and finding that out at the end of the flow costs the
     * user the entire flow. Blocking here costs them two seconds.
     */
    canShoot() {
      return this.cameraReady && (this.framed || this.manualUnlocked);
    },
  },

  async created() {
    // Not reactive: nothing renders from these.
    this.fallbackTimer = null;
    this.cued = false;

    // If access already exists — granted on an earlier visit to this screen, or returned
    // by the Permissions API after a reload — open the camera straight away. The browser
    // will not prompt, so priming would explain a prompt that never comes.
    if (this.$store.state.ui.mediaGranted || (await mediaAccessGranted())) {
      this.requestAccess();
    } else {
      this.gate = "priming";
    }
  },

  beforeDestroy() {
    this.cancelManualFallback();
  },

  methods: {
    /** The user tapped a button on our sheet; from here a denial is a real denial. */
    grant() {
      this.primed = true;
      this.requestAccess();
    },

    /**
     * Sound the cue the moment framing becomes correct.
     *
     * Once per run of good framing, not once per frame: the flag clears only when the face
     * leaves the oval again, so shifting slightly while framed stays silent.
     */
    onFraming(state) {
      this.framingHint = state.hint;
      // The first verdict is what starts the clock: before it, the runtime may still have
      // been downloading, and that wait is not the user failing to frame themselves.
      if (!state.framed) this.armManualFallback();

      if (state.framed && !this.cued) {
        this.cued = true;
        playReadyCue();
      } else if (!state.framed) {
        this.cued = false;
      }
    },

    requestAccess() {
      this.requesting = true;
      if (this.cameraActive) {
        // Retrying after a denial: `active` is already true, so the watcher will not
        // fire again and the request has to be made explicitly.
        this.$refs.camera.acquire();
      } else {
        // Flipping `active` is what triggers getUserMedia; the result comes back as
        // `ready` or `denied`.
        this.cameraActive = true;
      }
    },

    onCameraReady() {
      this.requesting = false;
      this.cameraReady = true;
      this.gate = "closed";
      this.$store.commit("ui/SET_MEDIA_GRANTED", true);
    },

    onFramed(framed) {
      this.framed = framed;
      if (framed) this.cancelManualFallback();
      else this.armManualFallback();
    },

    /**
     * No detector on this device — no WebGL, or the runtime never arrived.
     *
     * Waiting out the fallback timer would leave the user staring at a locked shutter for
     * no reason, since nothing is going to change. Hand it back now.
     */
    onDetectorUnavailable() {
      this.cancelManualFallback();
      this.manualUnlocked = true;
    },

    /**
     * Never let the check become a dead end.
     *
     * This is a public service people are required to use, and no detector is perfect: a
     * dark room, a headscarf shadowing the face, an unusual camera — any of these can keep
     * it from ever saying "framed". After a fair wait the button comes back and the
     * guidance turns into advice instead of a gate. Once handed back it stays back;
     * re-locking someone who is already struggling would be worse than the bad photo.
     *
     * Armed from the first verdict rather than from the camera opening, so the runtime's
     * own download does not eat the window — and re-armed every time framing is lost, so a
     * user who framed once and then moved is not stranded on a dead button.
     */
    armManualFallback() {
      if (this.manualUnlocked || this.fallbackTimer) return;
      this.fallbackTimer = setTimeout(() => {
        this.fallbackTimer = null;
        if (!this.framed) this.manualUnlocked = true;
      }, FRAMING_FALLBACK_MS);
    },

    cancelManualFallback() {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    },

    onCameraDenied() {
      this.requesting = false;
      this.cameraReady = false;
      // A silent attempt can fail merely because the browser wants a user gesture first,
      // and the recovery for that is the priming sheet, not settings instructions. Only
      // call it blocked once the request has failed from behind a tap.
      this.gate = this.primed ? "blocked" : "priming";
      if (this.primed) this.$store.commit("ui/SET_MEDIA_GRANTED", false);
    },

    shoot() {
      const dataUrl = this.$refs.camera.capture();
      if (!dataUrl) return;
      this.shot = dataUrl;
    },

    retake() {
      this.shot = "";
      this.cued = false;
      // The check runs again on the fresh preview, so the fallback window does too.
      this.armManualFallback();
    },

    confirm() {
      this.$store.commit("flow/SET_FACE", this.shot);
      if (this.editing) {
        this.$router.push({ name: "check-assets" });
      } else {
        this.$router.push({ name: FLOW_ORDER[1] });
      }
    },
  },
};
</script>

<style scoped>
.helper {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  font-size: 14px;
  margin-top: 14px;
  text-align: center;
}

.helper >>> svg {
  width: 17px;
  height: 17px;
  flex: 0 0 auto;
}

/* Shutter plus a narrow "guide" button beside it: the guide is worth one tap but must
   not compete with the primary action, so it takes a quarter of the row. */
.btnrow {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.btnrow > .btn {
  width: auto;
}

.btnrow > .guide {
  flex: 0 0 25%;
  min-width: 0;
  padding: 0 8px;
  font-size: 15px;
  gap: 6px;
}

.btnrow > .shoot {
  flex: 1 1 auto;
}
</style>
